import { ChatBubbleOvalLeftIcon } from '@heroicons/react/24/outline';
import { useEffect, useState } from 'react';
import { AiOutlineHome } from 'react-icons/ai';
import { BiCategoryAlt } from 'react-icons/bi';
import { CgProfile } from 'react-icons/cg';
import { FaMoneyBillWave, FaSignOutAlt, FaUserFriends, FaUsers, FaUserShield, FaMapMarkerAlt, FaCalendarCheck, FaInfoCircle } from 'react-icons/fa';
import { HiOutlineMenu } from 'react-icons/hi';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import FeedbackModal from '../../components/FeedbackModal';
import { useAdmin } from '../../contexts/admin';
import { useAuth } from '../../contexts/auth';
import { useFriendRequests } from '../../contexts/friendRequests';
import { useFriends } from '../../contexts/friends';
import Drawer from '../Drawer';
import NotificationsDropdown from '../NotificationsDropdown';
import './styles.css';

const Navbar = () => {
	const [isOpen, setIsOpen] = useState(false);
	const [showFeedbackModal, setShowFeedbackModal] = useState(false);
	const [isScrolled, setIsScrolled] = useState(false);
	const { signOut, user } = useAuth();
	const { isSuperAdmin } = useAdmin();
	const navigate = useNavigate();
	const location = useLocation();
	const { friendsCount } = useFriends();
	const { pendingCount, pendingRequests } = useFriendRequests();

	// Detectar scroll para adicionar efeito blur
	useEffect(() => {
		const handleScroll = () => {
			setIsScrolled(window.scrollY > 10);
		};
		window.addEventListener('scroll', handleScroll);
		return () => window.removeEventListener('scroll', handleScroll);
	}, []);

	const menuItems = [
		{
			to: "/home",
			label: "Home",
			icon: <AiOutlineHome className="w-5 h-5" />
		},
		{
			to: "/perfil",
			label: "Perfil",
			icon: <CgProfile className="w-5 h-5" />
		},
		{
			to: "/grupos",
			label: "Meus Grupos",
			icon: <FaUsers className="w-5 h-5" />
		},
		{
			to: "/rateios",
			label: "Rateios",
			icon: <FaMoneyBillWave className="w-5 h-5" />
		},
		{
			to: "/regras-sorteio",
			label: "Regras de Sorteio",
			icon: <BiCategoryAlt className="w-5 h-5" />
		},
		{
			to: "/amigos",
			label: "Amigos",
			icon: <FaUserFriends className="w-5 h-5" />
		},
		{
			to: "/sobre",
			label: "Sobre",
			icon: <FaInfoCircle className="w-5 h-5" />
		},
		{
			to: "#",
			label: "Enviar Feedback",
			icon: <ChatBubbleOvalLeftIcon className="w-5 h-5" />,
			onClick: () => {
				setShowFeedbackModal(true);
				setIsOpen(false); // fecha o drawer após clicar
			}
		}
	];

	// Adicionar menu de admin se for superadmin
	if (isSuperAdmin) {
		menuItems.push(
			{
				to: "/admin/usuarios",
				label: "Usuários",
				icon: <FaUserShield className="w-5 h-5 text-yellow-500" />,
				isAdminRoute: true
			},
			{
				to: "/admin/feedbacks",
				label: "Feedbacks",
				icon: <ChatBubbleOvalLeftIcon className="w-5 h-5 text-yellow-500" />,
				isAdminRoute: true
			},
			{
				to: "/quadras",
				label: "Quadras",
				icon: <FaMapMarkerAlt className="w-5 h-5" />
			},
			{
				to: "/quadras/minhas",
				label: "Minhas Quadras",
				icon: <FaMapMarkerAlt className="w-5 h-5 text-green-500" />
			},
			{
				to: "/reservas",
				label: "Minhas Reservas",
				icon: <FaCalendarCheck className="w-5 h-5" />
			}
		);
	}

	// Prevenir scroll quando drawer está aberto
	useEffect(() => {
		if (isOpen) {
			document.body.style.overflow = 'hidden';
		} else {
			document.body.style.overflow = 'unset';
		}
	}, [isOpen]);

	const handleLogout = () => {
		signOut();
		navigate('/');
	};

	return (
		<>
			<nav className={`fixed top-0 left-0 right-0 z-[60] transition-all duration-300 ease-in-out navbar-container navbar-shadow-fix
		${isScrolled
					? 'bg-gray-900/95 backdrop-blur-lg shadow-2xl border-b border-gray-700/50 navbar-shadow-scrolled'
					: 'bg-gradient-to-r from-gray-900/90 to-gray-800/90 backdrop-blur-md shadow-lg'
				}`}
				style={{ 
					boxShadow: isScrolled 
						? '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(255, 255, 255, 0.05)' 
						: '0 10px 25px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)' 
				}}
			>
				<div className="mx-auto max-w-7xl px-3 sm:px-4 lg:px-8">
					<div className="relative flex h-16 sm:h-20 items-center justify-between min-w-0">

						{/* Seção Esquerda: Menu button + Logo */}
						<div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0 logo-container-responsive">
							{/* Menu button - sempre visível à esquerda */}
							<div className="flex flex-shrink-0 z-50" style={{overflow: 'visible'}}>
								<button
									onClick={() => setIsOpen(true)}
									className="group relative inline-flex items-center justify-center rounded-xl p-3 sm:p-2.5 bg-transparent 
					text-gray-400 hover:bg-gray-700/50 hover:text-white micro-interaction focus-ring shadow-lg"
									aria-label="Abrir menu"
									style={{boxShadow: '0 4px 16px 0 rgba(0,0,0,0.18)'}}
								>
									<HiOutlineMenu className="w-6 h-6 sm:w-6 sm:h-6 transition-transform duration-300 group-hover:scale-110" />
									{pendingCount > 0 && (
										<span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold 
					  rounded-full w-4 h-4 flex items-center justify-center notification-badge">
											{pendingCount > 9 ? '9+' : pendingCount}
										</span>
									)}
								</button>
							</div>

							{/* Logo - centralizado na área restante */}
							<div className="flex-1 flex justify-center lg:justify-start lg:ml-6">
								<Link
									to="/home"
									className="flex-shrink-0 group hover-glow micro-interaction min-w-0"
								>
									<div className="flex items-center gap-1.5 sm:gap-2 min-w-0">
										<span className="text-white font-bold 
					  bg-gradient-to-r from-blue-400 via-purple-400 to-blue-500 
					  bg-clip-text text-transparent group-hover:from-blue-300 
					  group-hover:via-purple-300 group-hover:to-blue-400 transition-all duration-300
					  text-base sm:text-lg lg:text-xl xl:text-2xl logo-text-adaptive
					  logo-text-mobile">
											Meu Esporte
										</span>
									</div>
								</Link>
							</div>
						</div>

						{/* Seção Direita: Notificações + Perfil + Botão Sair */}
						<div className="flex items-center gap-2 flex-shrink-0">
							
							{/* Dropdown de Notificações */}
							<NotificationsDropdown />

							{/* Perfil do usuário */}
							<Link
								to="/perfil"
								className="flex items-center gap-2 p-2 lg:px-3 lg:py-2 rounded-lg lg:rounded-xl hover:bg-gray-700/50 
				  transition-all duration-300 group"
							>
								{user?.avatar ? (
									<img
										src={user.avatar}
										alt={user.nome || 'Usuário'}
										className="w-8 h-8 rounded-full border-2 border-gray-600 group-hover:border-blue-500 
					  transition-all duration-300"
									/>
								) : (
									<div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 
					flex items-center justify-center text-white text-sm font-semibold
					border-2 border-gray-600 group-hover:border-blue-500 transition-all duration-300">
										{user?.nome ? user.nome.charAt(0).toUpperCase() : 'U'}
									</div>
								)}
								<span className="hidden lg:block text-gray-300 group-hover:text-white text-sm font-medium transition-colors duration-300 truncate max-w-20">
									{user?.nome || 'Usuário'}
								</span>
							</Link>

							{/* Botão Sair */}
							<button
								onClick={handleLogout}
								className="hidden lg:block p-2.5 text-red-400 hover:text-red-300 hover:bg-red-500/10 
				  rounded-xl transition-all duration-300 group"
								aria-label="Sair"
							>
								<FaSignOutAlt className="w-4 h-4 transition-transform duration-300 group-hover:scale-110" />
							</button>
						</div>
					</div>
				</div>
			</nav>

			{/* Spacer para compensar navbar fixa */}
			<div className="h-16 sm:h-20" />

			<Drawer
				isOpen={isOpen}
				onClose={() => setIsOpen(false)}
				menuItems={menuItems}
				onLogout={handleLogout}
				pendingRequestsCount={pendingCount}
				user={user}
			/>

			<FeedbackModal
				isOpen={showFeedbackModal}
				onClose={() => setShowFeedbackModal(false)}
			/>
		</>
	);
};

export default Navbar;
